import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Brain, Sparkles, Activity, Heart, Users,
  AlertTriangle, CheckCircle, Clock, ChevronRight,
  Plus, Search, FileText, Target, TrendingUp,
  Stethoscope, UserCog, Shield, Zap, History
} from "lucide-react";

const AICarePlansPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Form state for HOPE assessment data
  const [formData, setFormData] = useState({
    diagnosis: '',
    current_loc: 'routine',
    admission_date: '',
    pain_level: 0,
    pain_location: '',
    dyspnea_level: 0,
    nausea_level: 0,
    anxiety_level: 0,
    fatigue_level: 0,
    functional_status: '',
    skin_status: 'Intact',
    mental_status: 'Oriented x3',
    caregiver_status: '',
    current_goals: '',
    current_interventions: '',
    request_focus: ''
  });

  // Redirect if not authorized
  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff', 'nurse', 'rn', 'physician', 'doctor', 'medical_director'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Load AI status and patients
  useEffect(() => {
    const loadData = async () => {
      // Load AI status separately to avoid one failure affecting the other
      try {
        const statusRes = await api.get('/ehr/ai-care-plans/status');
        setAiStatus(statusRes.data);
      } catch (error) {
        console.error("Failed to load AI status:", error);
        setAiStatus({ ai_available: false, model: 'rule-based fallback' });
      }
      
      // Load patients separately
      try {
        const patientsRes = await api.get('/ehr/charts/list');
        setPatients(patientsRes.data.charts || []);
      } catch (error) {
        console.error("Failed to load patients, using demo data:", error);
        // Demo patients
        setPatients([
          { id: 'chart_001', demographics: { first_name: 'John', last_name: 'Smith' }, diagnosis: 'Lung Cancer', loc: 'routine' },
          { id: 'chart_002', demographics: { first_name: 'Mary', last_name: 'Johnson' }, diagnosis: 'CHF', loc: 'routine' },
          { id: 'chart_003', demographics: { first_name: 'Robert', last_name: 'Williams' }, diagnosis: 'COPD', loc: 'continuous' },
        ]);
      }
    };
    loadData();
  }, []);

  // Load history when patient changes
  useEffect(() => {
    if (selectedPatient) {
      loadHistory(selectedPatient.id);
    }
  }, [selectedPatient]);

  const loadHistory = async (patientId) => {
    try {
      const response = await api.get(`/ehr/ai-care-plans/history/${patientId}`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to load history:", error);
      setHistory([]);
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient first");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patient_id: selectedPatient.id,
        chart_id: selectedPatient.id,
        diagnosis: formData.diagnosis || selectedPatient.diagnosis || 'Not specified',
        current_loc: formData.current_loc,
        admission_date: formData.admission_date || new Date().toISOString().split('T')[0],
        hope_assessment: {
          pain_level: formData.pain_level,
          pain_location: formData.pain_location || null,
          dyspnea_level: formData.dyspnea_level,
          nausea_level: formData.nausea_level,
          anxiety_level: formData.anxiety_level,
          fatigue_level: formData.fatigue_level,
          functional_status: formData.functional_status ? { notes: formData.functional_status } : null,
          skin_status: formData.skin_status || null,
          mental_status: formData.mental_status || null,
          caregiver_status: formData.caregiver_status || null
        },
        recent_exceptions: [],
        symptom_trends: [],
        current_goals: formData.current_goals ? formData.current_goals.split('\n').filter(g => g.trim()) : [],
        current_interventions: formData.current_interventions ? formData.current_interventions.split('\n').filter(i => i.trim()) : [],
        request_focus: formData.request_focus || null
      };

      const response = await api.post('/ehr/ai-care-plans/generate', payload);
      setRecommendations(response.data);
      toast.success("Care plan recommendations generated!");
      
      // Refresh history
      loadHistory(selectedPatient.id);
    } catch (error) {
      console.error("Failed to generate recommendations:", error);
      toast.error("Failed to generate recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAnalyze = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient first");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ehr/ai-care-plans/quick-analyze', null, {
        params: {
          patient_id: selectedPatient.id,
          pain_level: formData.pain_level,
          dyspnea_level: formData.dyspnea_level,
          recent_exception: formData.request_focus || null
        }
      });
      
      if (response.data.suggestions?.length > 0) {
        toast.success(`Found ${response.data.suggestions.length} quick suggestions`);
      } else {
        toast.info("No immediate concerns detected");
      }
    } catch (error) {
      console.error("Quick analyze failed:", error);
      toast.error("Quick analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter(p => {
    const name = `${p.demographics?.first_name} ${p.demographics?.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'symptom_management': return <Activity className="w-4 h-4" />;
      case 'functional': return <Target className="w-4 h-4" />;
      case 'psychosocial': return <Heart className="w-4 h-4" />;
      case 'caregiver': return <Users className="w-4 h-4" />;
      case 'loc_change': return <TrendingUp className="w-4 h-4" />;
      case 'goal_update': return <FileText className="w-4 h-4" />;
      default: return <Stethoscope className="w-4 h-4" />;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Brain className="w-8 h-8 text-purple-600" />
                AI Care Plan Recommendations
              </h1>
              <p className="text-muted-foreground mt-1">
                GPT-5.2 powered clinical decision support for hospice care planning
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge 
                variant={aiStatus?.ai_available ? "default" : "secondary"}
                className="px-3 py-1"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {aiStatus?.model || 'Loading...'}
              </Badge>
              <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
                Back to EHR
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Patient Selection & Assessment Input */}
          <div className="lg:col-span-1 space-y-6">
            {/* Patient Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5" />
                  Select Patient
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {filteredPatients.map(patient => (
                    <div
                      key={patient.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedPatient?.id === patient.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedPatient(patient);
                        setFormData(prev => ({
                          ...prev,
                          diagnosis: patient.diagnosis || '',
                          current_loc: patient.loc || 'routine'
                        }));
                      }}
                      data-testid={`patient-${patient.id}`}
                    >
                      <p className="font-medium">
                        {patient.demographics?.first_name} {patient.demographics?.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {patient.diagnosis || 'No diagnosis'} • {patient.loc?.toUpperCase() || 'Routine'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* HOPE Assessment Quick Entry */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5" />
                  Current Assessment
                </CardTitle>
                <CardDescription>Enter current patient status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Diagnosis</Label>
                  <Input
                    value={formData.diagnosis}
                    onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                    placeholder="e.g., Lung Cancer, Stage IV"
                  />
                </div>

                <div>
                  <Label>Level of Care</Label>
                  <Select 
                    value={formData.current_loc}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, current_loc: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine Home Care</SelectItem>
                      <SelectItem value="continuous">Continuous Care</SelectItem>
                      <SelectItem value="respite">Respite Care</SelectItem>
                      <SelectItem value="gip">General Inpatient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Pain Level: {formData.pain_level}/10</Label>
                  <Slider
                    value={[formData.pain_level]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, pain_level: value[0] }))}
                    max={10}
                    step={1}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Dyspnea Level: {formData.dyspnea_level}/3</Label>
                  <Slider
                    value={[formData.dyspnea_level]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, dyspnea_level: value[0] }))}
                    max={3}
                    step={1}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nausea: {formData.nausea_level}/3</Label>
                    <Slider
                      value={[formData.nausea_level]}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nausea_level: value[0] }))}
                      max={3}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Anxiety: {formData.anxiety_level}/3</Label>
                    <Slider
                      value={[formData.anxiety_level]}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, anxiety_level: value[0] }))}
                      max={3}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label>Focus Area (Optional)</Label>
                  <Input
                    value={formData.request_focus}
                    onChange={(e) => setFormData(prev => ({ ...prev, request_focus: e.target.value }))}
                    placeholder="e.g., Pain management, Caregiver support"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    className="flex-1"
                    onClick={handleGenerateRecommendations}
                    disabled={!selectedPatient || loading}
                    data-testid="generate-recommendations-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleQuickAnalyze}
                    disabled={!selectedPatient || loading}
                  >
                    <Zap className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Recommendations Display */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="generate" data-testid="tab-generate">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Recommendations
                </TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">
                  <History className="w-4 h-4 mr-2" />
                  History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="generate" className="space-y-4">
                {recommendations ? (
                  <>
                    {/* Summary Card */}
                    <Card className="border-purple-200 bg-purple-50/50">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Brain className="w-5 h-5 text-purple-600" />
                              <span className="font-semibold">AI Analysis Summary</span>
                              <Badge variant="outline" className="text-xs">
                                {recommendations.ai_model}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              {recommendations.summary}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="secondary">
                                {recommendations.recommendations?.length || 0} Recommendations
                              </Badge>
                              {recommendations.loc_recommendation && (
                                <Badge variant="destructive">
                                  LOC Review Suggested
                                </Badge>
                              )}
                              <Badge variant="outline">
                                <Clock className="w-3 h-3 mr-1" />
                                Review: {recommendations.next_review_suggested}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {recommendations.overall_assessment && (
                          <div className="mt-4 p-3 bg-white rounded-lg border">
                            <p className="text-sm font-medium text-purple-800">
                              Overall Assessment
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {recommendations.overall_assessment}
                            </p>
                          </div>
                        )}

                        {recommendations.loc_recommendation && (
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Level of Care Recommendation
                            </p>
                            <p className="text-sm text-amber-700">
                              {recommendations.loc_recommendation}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Recommendations List */}
                    <div className="space-y-3">
                      {recommendations.recommendations?.map((rec, idx) => (
                        <Card key={rec.id || idx} className="hover:shadow-md transition-shadow">
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${
                                rec.priority === 'high' ? 'bg-red-100' :
                                rec.priority === 'medium' ? 'bg-amber-100' : 'bg-green-100'
                              }`}>
                                {getCategoryIcon(rec.category)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="font-semibold">{rec.title}</h4>
                                  <div className="flex gap-2">
                                    <Badge variant={getPriorityColor(rec.priority)}>
                                      {rec.priority}
                                    </Badge>
                                    <Badge variant="outline">
                                      {rec.timeframe?.replace('_', ' ')}
                                    </Badge>
                                  </div>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {rec.description}
                                </p>
                                
                                {rec.rationale && (
                                  <div className="bg-slate-50 p-2 rounded text-xs text-slate-600 mb-2">
                                    <strong>Rationale:</strong> {rec.rationale}
                                  </div>
                                )}

                                {rec.suggested_intervention && (
                                  <div className="bg-blue-50 p-2 rounded text-xs text-blue-700 mb-2">
                                    <strong>Suggested Intervention:</strong> {rec.suggested_intervention}
                                  </div>
                                )}

                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-muted-foreground">Disciplines:</span>
                                  {rec.discipline?.map((d, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {d}
                                    </Badge>
                                  ))}
                                  {rec.requires_md_approval && (
                                    <Badge variant="secondary" className="text-xs">
                                      <Shield className="w-3 h-3 mr-1" />
                                      MD Approval
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Brain className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                      <h3 className="text-lg font-medium mb-2">No Recommendations Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Select a patient and enter their current assessment data, then click Generate to receive AI-powered care plan recommendations.
                      </p>
                      <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          HOPE Assessment Analysis
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Symptom Trend Detection
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Evidence-Based Suggestions
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="history">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Recommendation History
                    </CardTitle>
                    <CardDescription>
                      Previous AI-generated care plan recommendations for {selectedPatient ? 
                        `${selectedPatient.demographics?.first_name} ${selectedPatient.demographics?.last_name}` : 
                        'selected patient'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!selectedPatient ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Select a patient to view their recommendation history</p>
                      </div>
                    ) : history.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No recommendation history for this patient</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {history.map((entry, idx) => (
                          <div 
                            key={idx}
                            className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                            onClick={() => setRecommendations(entry.response)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">
                                {new Date(entry.created_at).toLocaleDateString()} at {new Date(entry.created_at).toLocaleTimeString()}
                              </span>
                              <Badge variant="outline">
                                {entry.response?.ai_model || 'Unknown'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {entry.response?.recommendations?.length || 0} recommendations generated
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {entry.response?.summary}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICarePlansPage;
